import { Button, Tooltip, theme } from "antd";
import { EditOutlined, LinkOutlined, CheckOutlined } from "@ant-design/icons";
import { Flex } from "src/components/Flex";
import { FavoriteButton } from "src/components/FavoriteButton";
import { CopyButton } from "src/components/CopyButton";
import { useRouter } from "next/router";
import { ItemType } from "src/components/useLibrary";
import {
  serializeParams,
  ensureState,
} from "src/search/lib/params-state-reducer";
import { DateRangeSelect } from "src/components/DateRangeSelect";
import { Duration } from "src/utils/date";
import { TeamSelector } from "pages/[orgId]/teams/components/TeamSelector";
import { useQuery } from "react-query";
import { readTeams, readTeamsQueryKey } from "pages/api-clients/teams";
import { DigestV2State } from "pages/api/deep-links/types";
import { AnnotatedDeepLink } from "pages/api/deep-links/types";

export function TopicHeader(props: {
  title: string;
  link: AnnotatedDeepLink<DigestV2State>;
  topic: any;
  canEdit: boolean;
  onUpdateFilters?: (filters: any[]) => void;
  isLoading?: boolean;
  onUpdateTimeframe?: (dateRange: any) => void;
}) {
  // Always call hooks first
  const router = useRouter();
  const orgId = Array.isArray(router.query.orgId)
    ? router.query.orgId[0]
    : router.query.orgId || "";

  // Always call useQuery, even if orgId is missing
  const teamsQuery = useQuery(
    orgId ? readTeamsQueryKey(orgId) : ["teams", "no-org"],
    () => (orgId ? readTeams(orgId) : Promise.resolve({ data: [] })),
    { enabled: !!orgId }
  );
  const teams = teamsQuery.data?.data || [];

  // Now destructure props
  const {
    title,
    link,
    topic,
    canEdit,
    onUpdateFilters,
    isLoading,
    onUpdateTimeframe,
  } = props;

  // Now do the early return
  if (link === null || link.json === undefined) return null;

  const topicId = Array.isArray(link?.id) ? link.id[0] : link?.id || "";
  const serialized = serializeParams(
    ensureState((link.json as any)?.params || {})
  );
  const topicUrl =
    typeof window === "undefined"
      ? ""
      : `${window.location.origin}/${orgId}/topics/${topicId}?s=${serialized}`;

  const selectedTeamId =
    typeof router.query.teamId === "string" ? router.query.teamId : undefined;

  // Handler for team change
  const handleTeamChange = (teamIds: string | string[]) => {
    const teamId = Array.isArray(teamIds) ? teamIds[0] : teamIds;
    const team = teams.find((t) => t.id === teamId);
    const newFilters = teamId
      ? [
          {
            key: "Activity.Team",
            target: "Activity",
            attribute: "Team",
            comparison: { IN: [team?.name] },
          },
        ]
      : [];
    onUpdateFilters?.(newFilters);
  };

  // Handler for timeframe change (calls parent handler)
  const handleTimeframeChange = ({
    startDate,
    endDate,
    duration,
  }: {
    startDate?: string;
    endDate?: string;
    duration?: Duration;
  }) => {
    if (onUpdateTimeframe) {
      onUpdateTimeframe({
        startDate: startDate ?? topic?.defaultDateRange?.startDate,
        endDate: endDate ?? topic?.defaultDateRange?.endDate,
        duration: duration ?? topic?.defaultDateRange?.duration,
      });
    }
  };

  return (
    <Flex
      className="vzbl-header-bar"
      style={{ justifyContent: "space-between" }}
    >
      <div
        style={{
          minWidth: 0,
          maxWidth: "100%",
          display: "flex",
          alignItems: "center",
        }}
        className="vzbl-text-6 line-clamp-1"
      >
        {title || "Untitled Topic"}
        {isLoading && (
          <span style={{ marginLeft: 12 }}>
            <span className="ant-spin ant-spin-sm" />
          </span>
        )}
      </div>
      <div
        style={{
          marginLeft: 16,
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <DateRangeSelect
          startDate={topic?.defaultDateRange?.startDate}
          endDate={topic?.defaultDateRange?.endDate}
          duration={topic?.defaultDateRange?.duration}
          onChangeDuration={(newDuration) =>
            handleTimeframeChange({ duration: newDuration })
          }
          onChangeRange={([newStart, newEnd]) =>
            handleTimeframeChange({ startDate: newStart, endDate: newEnd })
          }
          style={{ minWidth: 80 }}
        />
        <div style={{ minWidth: 80 }}>
          <TeamSelector
            teams={teams}
            value={selectedTeamId}
            onChange={handleTeamChange}
            placeholder="Filter by team..."
            loading={teamsQuery.isLoading}
          />
        </div>
      </div>
      <div>
        <Flex
          style={{
            gap: 8,
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <CopyButton
            text={topicUrl}
            icon={<LinkOutlined />}
            clickedIcon={<CheckOutlined />}
            type="text"
            label={null}
            tooltip={"Copies a direct link to this instance of the topic"}
          />
          <FavoriteButton item={{ ...topic, itemType: ItemType.TOPICS }} />
          {canEdit === true && (
            <Tooltip title="Edit topic">
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  router.push({
                    pathname: "/[orgId]/topics/[topicId]/edit.v2",
                    query: { orgId, topicId },
                  });
                }}
              />
            </Tooltip>
          )}
        </Flex>
      </div>
    </Flex>
  );
}
